import { useEffect, useState } from 'react';
import { listOwnedCasinos } from './casinoApi.js';
import { getSession, type SessionUser } from './session.js';

export interface CasinoOwnership {
  /** True when this browser is signed in as the owner of `joinCode`. */
  owns: boolean;
  /**
   * The owner's reserved seat name, once known. The phone joins under it
   * instead of asking for a nickname — the server insists on it anyway.
   */
  playerName: string | null;
  /**
   * Whoever is signed in on this browser, owner or not. The join page names
   * them, and the server badges their seat — a guest phone gets null.
   */
  session: SessionUser | null;
}

const SIGNED_OUT: CasinoOwnership = { owns: false, playerName: null, session: null };

/**
 * Who is signed in on this browser, and whether that account owns `joinCode`
 * and the name it plays under. Used by Play and the TV so Deal / settings
 * appear on the live table, not only on the /casino console. Anonymous or
 * failed reads stay signed out.
 */
export function useOwnsCasino(joinCode: string | null): CasinoOwnership {
  const [ownership, setOwnership] = useState<CasinoOwnership>(SIGNED_OUT);

  useEffect(() => {
    let stale = false;
    void (async () => {
      const session = await getSession().catch(() => null);
      if (!session) {
        if (!stale) setOwnership(SIGNED_OUT);
        return;
      }
      if (!joinCode) {
        if (!stale) setOwnership({ owns: false, playerName: null, session });
        return;
      }
      const owned = await listOwnedCasinos().catch(() => []);
      if (stale) return;
      const owns = owned.some((c) => c.joinCode === joinCode);
      setOwnership({ owns, playerName: (owns && session.playerName) || null, session });
    })();
    return () => {
      stale = true;
    };
  }, [joinCode]);

  return ownership;
}
